import { supabase } from '../../../common/src/supabase';

export type ActionType = 
  | 'create'
  | 'update'
  | 'delete'
  | 'login'
  | 'logout'
  | 'approve'
  | 'reject'
  | 'process'
  | 'upload'
  | 'view';

export type EntityType =
  | 'user'
  | 'settlement'
  | 'email'
  | 'excel_rule'
  | 'exporter'
  | 'receiver'
  | 'page'

export const logUserAction = async (
  actionType: ActionType,
  entityType: EntityType,
  entityId?: string | number,
  details?: Record<string, any>
) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      throw new Error('No authenticated user found');
    }

    // Get device information
    const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|Windows Phone/i.test(navigator.userAgent);
    const deviceInfo = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      vendor: navigator.vendor,
      deviceType: isMobile ? 'mobile' : 'desktop'
    };

    // Merge device info with provided details
    const enhancedDetails = {
      ...details,
      device: deviceInfo
    };

    // First get the user record using auth_id
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id')
      .eq('auth_id', session.user.id)
      .single();

    if (userError || !userData) {
      throw new Error('User not found');
    }

    const { error } = await supabase.from('actions').insert({
      user_id: userData.id,
      action_type: actionType,
      entity_type: entityType,
      entity_id: entityId?.toString(),
      details: enhancedDetails
    });

    if (error) throw error;
  } catch (error) {
    console.error('Error logging action:', error);
  }
}; 

interface GetUserActionsParams {
  entityId?: string;
  action?: string;
  limit?: number;
}

interface UserAction {
  id: string;
  user_id: string;
  action: string;
  entity_id: string;
  entity_type: string;
  details?: {
    id?: string;
    incoterm?: string;
    [key: string]: any;
  };
  created_at: string;
}

export const getUserActions = async ({
  entityId,
  limit = 1000
}: GetUserActionsParams): Promise<UserAction[]> => {
  try {
    // Get current user's session
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      throw new Error('No authenticated user found');
    }

    // Get user's ID from users table
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id')
      .eq('auth_id', session.user.id)
      .single();

    if (userError || !userData) {
      throw new Error('User not found');
    }

    let query = supabase
      .from('actions')
      .select('*')
      .eq('user_id', userData.id) // Filter by user_id
      .order('created_at', { ascending: false })
      .limit(limit);

    if (entityId) {
      query = query.eq('entity_id', entityId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching user actions:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getUserActions:', error);
    return [];
  }
}; 